import { Card } from '@uhg-abyss/web/ui/Card';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { FindhelpProgram } from '@src/api.types';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useState, useEffect } from 'react';
import { Box } from '@uhg-abyss/web/ui/Box';
import { styled } from '@uhg-abyss/web/tools/styled';
import { getAddressText, getOpenHoursText } from '@src/utills/utils';
import { capitlalizeWordFirstLetter, formatCostField } from '@repo/utils';
import { Button, CboCardFindhelpNextSteps } from '@repo/ui';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Features, getFeatureFlags } from '../../utills/features';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { SaveFavoritePanel } from './SaveFavoritePanel';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { FeatureFlags } from '../../../../../packages/utils/src/schemas/flagSmith';

export type CboCardFindhelpProps = {
  // Program data.
  program: FindhelpProgram;
  allCategories:any[];
  // Callback trigger when the user clicks 'Refer' / 'Log referral' button
  onClickRefer: () => void;

  // Whether the program is currently in the user's 'bulk referral' cart.
  // Indicates whether the 'bulk referral' checkbox is checked.
  isBulkReferring: boolean;

  // Callback trigger when the user clicks the 'bulk referral' checkbox to
  // add or remove this program from their bulk referral 'cart'.
  onClickBulkReferToggle: () => void;

  // Callback trigger when the user clicks the program title.
  // Intended behavior is to open the 'details' modal for this program.
  onClickTitle: () => void;

  // Triggered when user clicks a specific service provided by the program.
  // Intended behavior is to modify their search to look for programs
  // which also provide this service.
  onClickService: (service: string,id:any) => void;

  // Triggered when the user clicks a specific attribute tag (of the populations
  // this program serves).
  // Intended behavior is to add this attribute to the filters of the current search.
  onClickServingAttribute: (attribute: string) => void;

  // Triggered when user clicks print button. Opens the browser's print dialog for this program.
  onClickPrint: () => void;

  // Names of favorites folders the user has already created.
  availableFavoritesFolders: string[];

  // Triggered when the user clicks the favorite button and adds the program to a favorites folder.
  // `inFolder` is the name of the folder to save the program in. Whether this folder
  // already exists or is being created for the first time is not relevant.
  onSaveFavorite: (inFolder: string) => void;

  // If this program is already in one of the user's favorites folders.
  isFavorite: boolean;

  // Triggered when the user removes this program from a favorites folder.
  // NOTE - The behavior of this seems strange to me, if a program is in multiple
  // favorites folders it seems like it is removed from all of them?
  onRemoveFavorite: () => void;

  // Triggered when the user clicks the email button.
  // Intended behavior is to open the email modal to send the program details to the member.
  onClickEmail: () => void;

  // Whether bulk referrals are enabled.
  showBulkReferralCheckbox: boolean;
  member
};

const InvisibleDivider = styled('hr', { border: 'none' });
const PseudoLinkButton = ({ onClick, height, style, children }) => {
  return (
    <Button
      variant="text"
      style={{
        padding: 0,
        height: height || 'auto',
        ...style,
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

function ResourceRecommendationText({
  wlScore,
}: {
  wlScore?: number;
}): React.JSX.Element {
  const style = {
    paddingTop: 2,
    paddingBottom: 2,
    marginBottom: 4,
  };
  return (
    <React.Fragment>
      {wlScore === 100 && (
        <div className="tag-class" style={style} color="#424242">
          <Badge variant="success" outline >
            Recommended Resource
          </Badge>
        </div>
      )}

      {wlScore === 150 && (
        <div className="tag-class" style={style} color="#424242">
          <Badge variant="warning" outline >
            Value Add Benefit
          </Badge>
        </div>
      )}

      {wlScore === 200 && (
        <div className="tag-class" style={style} color="#424242">
          <Badge variant="info" outline >
            Trusted Network Partner
          </Badge>
        </div>
      )}

      {wlScore !== 100 && wlScore !== 200 && wlScore !== 150 && <React.Fragment />}
    </React.Fragment>
  );
}

export function CboCardFindhelp({
  program,
  allCategories,
  onClickRefer,
  isBulkReferring,
  onClickBulkReferToggle,
  onClickTitle,
  onClickService,
  onClickServingAttribute,
  onClickPrint,
  onClickEmail,
  isFavorite,
  availableFavoritesFolders,
  onSaveFavorite,
  onRemoveFavorite,
  showBulkReferralCheckbox,
  member
}: CboCardFindhelpProps): React.JSX.Element {
  // Visibility of email is controlled via feature flag.
  const processedCost = formatCostField(program?.free_or_reduced);

  const featureFlags: Features = getFeatureFlags();
  const featureFlagsFromStorage: FeatureFlags = getFeatureFlagsFromStorage();
  const [descriptionExpanded, setDescriptionExpanded] =
    useState<boolean>(false);
  const [programInfoExpanded, setProgramInfoExpanded] =
    useState<boolean>(false);
  const [hoursExpanded, setHoursExpanded] = useState<boolean>(false);
  const [favoriteExpanded, setFavoriteExpanded] = useState<boolean>(false);


// Function to find category ID by label (case-insensitive)
function findCategoryIdByLabel(
  label: string,
  categories: any[]
): string | null {
  const target = label.toLowerCase();

  for (const category of categories) {
    if (category.label.toLowerCase() === target) {
      return category.id;
    }

    if (category.children?.length) {
      const result = findCategoryIdByLabel(label, category.children);
      if (result) return result;
    }
  }

  return null;
}

  return (
    <Card data-cy={`cbo-card-${program.id}`} css={{ 'abyss-card-root': { padding: 0, overflow: 'hidden' } }}>
      <Box color="white" padding={0}>
        <Grid space={0}>
          <Grid.Col
            span={'68%'}
            style={{ padding: 24 }}
          >
            {/* Use the wl_score to show whether the program is recommended / trusted */}
            <ResourceRecommendationText wlScore={program.wl_score} />

            <Flex direction="row" style={{ alignItems: 'center', gap: '8px' }}>
              {/* Bulk referral checkbox */}
              {
                showBulkReferralCheckbox && (
                  <Checkbox
                    isChecked={isBulkReferring}
                    onChange={onClickBulkReferToggle}
                  />
                )}
              {/* Program name & CBO name */}
              <Button
                onClick={onClickTitle}
                variant="text"
                style={{
                  height: 'auto',
                  textAlign: 'left',
                  padding: 0,
                  wordWrap: 'break-word',
                }}
                data-cy={`cbo-card-title-button-${program.id}`}
              >
                <Heading
                  data-cy="program-label"
                  size="sm"
                  color="$core.color.brand.60"
                >
                  {program.name}
                </Heading>
              </Button>
            </Flex>

            {program.provider_name && (
              <React.Fragment>
                <Text fontWeight="regular">by &nbsp;</Text>
                <Text color="$core.color.brand.60" fontWeight="regular" size="md">
                  {program.provider_name}
                </Text>
              </React.Fragment>
            )}

            <InvisibleDivider />

            {/* Description */}
            <Text>
              <span
                dangerouslySetInnerHTML={{ __html: program.description }}
                style={
                  // If the description is too long and not currently expanded,
                  // rely on CSS styling to truncate it and append the '...' to indicate
                  // it can be expanded
                  descriptionExpanded
                    ? undefined
                    : {
                      lineHeight: 1.5,
                      maxWidth: '100%',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }
                }
              ></span>
            </Text>

            <InvisibleDivider />

            {program.description.trim().split(/\s+/).length > 26 && (
              <PseudoLinkButton
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                height={20}
              >
                {descriptionExpanded ? 'Show less' : 'Show more'}
              </PseudoLinkButton>
            )}

            <InvisibleDivider
              style={{
                padding: 4,
              }}
            />

            {/* Services */}
            <label style={{ fontWeight: 'bold' }}>
              Main services:&nbsp;
              {program?.service_tags?.map((service, idx) => (
                <React.Fragment key={idx}>
                  <PseudoLinkButton
                    onClick={() => onClickService(service, findCategoryIdByLabel(service, allCategories))}
                    height={20}
                    style={{ minHeight: 0, lineHeight: 1.15, verticalAlign: 'baseline' }}
                  >
                    {capitlalizeWordFirstLetter(service)}
                  </PseudoLinkButton>{idx !== program.service_tags.length - 1 ? ', ' : ''}
                </React.Fragment>
              ))}
            </label>

            <InvisibleDivider
              style={{
                padding: 8,
              }}
            />

            {/* Attribute tags / serving populations */}
            <label style={{ fontWeight: 'bold' }}>
              Serving:&nbsp;
              {program?.attribute_tags?.map((tag, idx) => {
                const formattedTag = capitlalizeWordFirstLetter(tag);
                return (
                  <React.Fragment key={idx}>
                    <PseudoLinkButton
                      onClick={() => onClickServingAttribute(formattedTag)}
                      height={20}
                      style={{ minHeight: 0, lineHeight: 1.15, verticalAlign: 'baseline' }}
                    >
                      {formattedTag}
                    </PseudoLinkButton>{idx !== program.attribute_tags.length - 1 ? ', ' : ''}
                  </React.Fragment>
                );
              })}
            </label>

            {/* Additional info - Cost & language */}
            {programInfoExpanded && (
              <React.Fragment>
                <InvisibleDivider
                  style={{
                    padding: 4,
                  }}
                />

                <label style={{ fontWeight: 'bold' }}>
                  Cost:&nbsp;
                  <span style={{ fontWeight: 'normal' }}>
                    {processedCost}
                  </span>
                </label>

                <InvisibleDivider
                  style={{
                    padding: 4,
                  }}
                />

                <label style={{ fontWeight: 'bold' }}>
                  Language:&nbsp;
                  <span style={{ fontWeight: 'normal' }}>
                    {program.supported_languages.join(', ')}
                  </span>
                </label>
              </React.Fragment>
            )}

            <InvisibleDivider
              style={{
                padding: 4,
              }}
            />

            {/* Program hours */}
            <PseudoLinkButton
              onClick={() => setHoursExpanded(!hoursExpanded)}
              height={20}
            >
              {hoursExpanded ? 'Hide' : 'See'} open hours
              <IconSymbol icon={hoursExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
            </PseudoLinkButton>
            {hoursExpanded && (
              <OpenHoursInfo
                // TODO - Probably shouldn't just grab the first office when they might have multiple.
                office={program.offices[0]}
              />
            )}

            {/* Receives referrals? */}
            <Flex
              alignItems="center"
              style={{
                paddingTop: 8,
                gap: '6px',
              }}
            >
              <IconSymbol
                icon={
                  program.accepts_referrals ? 'verified' : 'do_not_disturb_on'
                }
                style={{ color: program.accepts_referrals ? '#007000' : '#E4780C' }}
              />
              <Text
                color={program.accepts_referrals ? '#007000' : '#E4780C'}
                fontWeight="bold"
                fontSize="md"
              >
                This organization{' '}
                {program.accepts_referrals ? 'receives' : 'does not receive'}{' '}
                member information
              </Text>

              {
                // Show a popover explaining what it means to log a referral when
                // the program does not accept referrals.
                program.accepts_referrals === false && (
                  <Popover
                    content="
                      Log referrals to contribute to our referral program
                      and enable essential follow-up actions to support
                      the member. When logging a referral, member
                      information is not sent to the organization.
                      Instead, share the organization's details with the
                      member.
                    "
                  ></Popover>
                )
              }
            </Flex>

            {/* Bottom-left corner: Interaction buttons */}
            <Layout.Group alignLayout="between">
              <Flex
                alignItems="center"
                style={{
                  paddingTop: 8,
                }}
              >
                <Button
                  data-cy="cbo-card-print-button"
                  onClick={() => onClickPrint()}
                  variant="text"
                  style={{
                    paddingLeft: 0,
                    paddingRight: 8,
                  }}
                >
                  <IconSymbol icon="print" variant="outlined" />
                </Button>
                <Button
                  data-cy="cbo-card-email-button"
                  onClick={() => onClickEmail()}
                  variant="text"
                  style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                  }}
                >
                  <IconSymbol icon="mail" variant="outlined" />
                </Button>

                <Button
                  variant="text"
                  data-cy="cbo-card-favorite-button"
                  onClick={() => {
                    if (isFavorite) {
                      onRemoveFavorite();
                      return;
                    }

                    setFavoriteExpanded(true);
                  }}
                  style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                  }}
                >
                  <IconSymbol
                    icon="favorite"
                    variant={isFavorite ? 'filled' : 'outlined'}
                  />
                  {isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                </Button>
              </Flex>

              <PseudoLinkButton
                onClick={() => setProgramInfoExpanded(!programInfoExpanded)}
                height={20}
                data-cy="cbo-card-show-more-button"
              >
                Show {programInfoExpanded ? 'less' : 'more'}
              </PseudoLinkButton>
            </Layout.Group>

            {favoriteExpanded && (
              <SaveFavoritePanel
                // TODO
                storedFolders={availableFavoritesFolders}
                onCancel={() => setFavoriteExpanded(false)}
                onSave={(folder: string) => {
                  setFavoriteExpanded(false);
                  onSaveFavorite(folder);
                }}
              />
            )}
          </Grid.Col>

          {/* Right-side: Next steps & referral button */}
          <Grid.Col
            span={'32%'}
            style={{
              backgroundColor: '#eef8fb', // Light blue
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: 24,
            }}
          >
            <CboCardFindhelpNextSteps
              nextStepsData={program.next_steps}
              firstOfficeAddressText={getAddressText(program)}
              firstOfficeEmail={program.offices[0].email ?? ''}
              firstOfficePhone={program.offices[0].phone_number ?? ''}
              firstOfficeWebsite={program.offices[0].website_url ?? ''}
              source='advocate'
            />

            <Button
              data-cy="refer-button"
              onClick={onClickRefer}
              style={{
                width: '100%',
              }}
            >
              {program.accepts_referrals ? 'Refer' : 'Log referral'}
            </Button>
          </Grid.Col>
        </Grid>
      </Box>
    </Card>
  );
}

export function OpenHoursInfo({ office }): React.JSX.Element {
  return (
    <div style={{ lineHeight: 1.5 }}>
      {(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const).map((day) => (
        <div key={day} style={{ display: 'flex', gap: '8px' }}>
          <span style={{ fontWeight: 'bold', minWidth: '100px' }}>{day}:</span>
          <span>{getOpenHoursText(office, day.toLowerCase()).replace(/\b0(\d:)/g, '$1')}</span>
        </div>
      ))}
    </div>
  );
}
